import React, { useState } from 'react';

export default function ManagedITServices() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companySize: '10-50 employees',
    serviceInterest: 'Complete IT Management',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans selection:bg-orange-500 selection:text-white">

      {/* ==========================================
          SECTION 1: HERO / MANAGED IT OPERATIONS
         ========================================== */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28 border-b border-orange-100">
        {/* Decorative Background Gradients */}
        <div className="absolute top-1/3 left-1/4 -z-10 w-96 h-96 bg-orange-400/15 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute -top-20 -right-20 -z-10 w-96 h-96 bg-amber-300/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f973160a_1px,transparent_1px),linear-gradient(to_bottom,#f973160a_1px,transparent_1px)] bg-[size:28px_28px]" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 font-bold text-xs tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              24/7 Managed IT Operations
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.1]">
              Managed IT Services <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500">
                To Keep You Running Smoothly
              </span>
            </h1>

            <p className="text-lg text-stone-600 max-w-xl leading-relaxed">
              End-to-end IT support, network monitoring, and proactive system management designed to eliminate downtime and keep your team focused on growth.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#managed-form"
                className="px-8 py-4 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all duration-200 shadow-xl shadow-orange-500/25 hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
              >
                Get Free IT Assessment
              </a>
              <a
                href="#services-section"
                className="px-8 py-4 rounded-xl bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-800 font-semibold transition-all duration-200"
              >
                Explore Services
              </a>
            </div>

            {/* Impact Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-orange-100">
              <div>
                <p className="text-3xl font-extrabold text-stone-900">99.9%</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Uptime Guaranteed</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-orange-600">&lt; 15m</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Response Time</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-stone-900">24 / 7</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Help Desk Support</p>
              </div>
            </div>
          </div>

          {/* Right Visual Monitoring Dashboard Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-stone-900 text-white border border-stone-800 rounded-3xl p-6 shadow-2xl">
              <div className="flex items-center justify-between pb-4 border-b border-stone-800">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs font-mono text-stone-300">System Status: All Systems Operational</span>
                </div>
                <span className="text-[11px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30 px-2.5 py-0.5 rounded-full">
                  Live NOC Telemetry
                </span>
              </div>

              {/* Simulated Infrastructure Health */}
              <div className="py-6 space-y-5">
                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Help Desk Response Index</span>
                    <span className="text-orange-400 font-bold">98.4% Resolution</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[98%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Network & Server Health</span>
                    <span className="text-orange-400 font-bold">100% Active</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[100%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Automated Backup Completion</span>
                    <span className="text-orange-400 font-bold">Verified Today</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[92%]" />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-stone-950 rounded-xl border border-stone-800 flex items-center justify-between text-xs font-mono">
                <span className="text-stone-400">Proactive Monitoring Standard</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  ISO & SLA Compliant
                </span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ==========================================
          SECTION 2: MANAGED IT SERVICES & CAPABILITIES
         ========================================== */}
      <section id="services-section" className="py-24 bg-orange-50/40">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-orange-600 font-bold text-xs uppercase tracking-widest bg-orange-100 border border-orange-200 px-3 py-1 rounded-full">
              Full-Suite Support
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Comprehensive Managed IT Services
            </h2>
            <p className="text-stone-600 text-base">
              Proactive IT management solutions tailored to secure your operations, reduce tech headaches, and maximize productivity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                24/7 Unlimited Help Desk
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Direct access to certified technicians around the clock to resolve user issues, hardware bugs, and software glitches instantly.
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Proactive Network Monitoring
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Continuous network auditing and telemetry to detect and fix performance bottlenecks before they interrupt operations.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Data Backup & Disaster Recovery
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Automated encrypted cloud backups and rapid disaster recovery planning to safeguard your business-critical data.
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Endpoint Security & Patching
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Centralized antivirus management, regular OS security patching, and vulnerability management across all company hardware.
              </p>
            </div>

            {/* Service 5 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cloud & SaaS Management
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Full administration for Microsoft 365, Google Workspace, and cloud instances including license management and access control.
              </p>
            </div>

            {/* Service 6 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Virtual CIO (vCIO) Leadership
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Strategic tech advisory to align IT budgeting, vendor management, and long-term hardware life cycles with business expansion.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          SECTION 3: ONBOARDING PROCESS & CONSULTATION
         ========================================== */}
      <section id="managed-form" className="py-24 bg-white border-t border-orange-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 text-white rounded-3xl p-8 lg:p-14 shadow-2xl relative overflow-hidden">
            
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Seamless Onboarding Process */}
              <div className="lg:col-span-6 space-y-6">
                <span className="bg-orange-500/10 text-orange-400 border border-orange-500/30 font-semibold text-xs px-3.5 py-1.5 rounded-full uppercase tracking-wider">
                  Frictionless Transition
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                  How We Take the Friction Out of Managed IT
                </h2>
                <p className="text-stone-400 text-base leading-relaxed">
                  Transitioning to our managed IT platform is smooth, structured, and designed for zero downtime during setup.
                </p>

                {/* Steps */}
                <div className="space-y-6 pt-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      01
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Infrastructure Audit</h4>
                      <p className="text-sm text-stone-400 mt-0.5">We assess network topology, software licenses, security gaps, and device health.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      02
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Onboarding & Tool Deployment</h4>
                      <p className="text-sm text-stone-400 mt-0.5">We deploy light agent monitoring, security protection, and help desk tools to all endpoints.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      03
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">24/7 Active Management</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Your staff gets instantaneous help desk access while our NOC proactively monitors systems.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Interactive Consultation Form */}
              <div className="lg:col-span-6">
                <div className="bg-white text-stone-900 border border-orange-100 rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <h3 className="text-2xl font-bold text-stone-900 mb-1">
                    Book a Free IT Infrastructure Audit
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Discover how our managed IT services can streamline your business operations.
                  </p>

                  {submitted ? (
                    <div className="bg-orange-50 border border-orange-200 text-stone-800 p-8 rounded-xl text-center space-y-3">
                      <div className="w-14 h-14 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto text-2xl font-bold shadow-lg shadow-orange-600/30">
                        ✓
                      </div>
                      <h4 className="font-bold text-xl text-stone-900">Request Received!</h4>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        An IT specialist will reach out within 24 hours to schedule your comprehensive assessment.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Jordan Lee"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Work Email
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="jordan@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Company Size
                        </label>
                        <select
                          value={formData.companySize}
                          onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="1-10 employees">1-10 employees</option>
                          <option value="10-50 employees">10-50 employees</option>
                          <option value="50-250 employees">50-250 employees</option>
                          <option value="250+ employees">250+ employees</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Primary IT Need
                        </label>
                        <select
                          value={formData.serviceInterest}
                          onChange={(e) => setFormData({ ...formData, serviceInterest: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="Complete IT Management">Complete IT Management</option>
                          <option value="24/7 Help Desk & Support">24/7 Help Desk & Support</option>
                          <option value="Data Backup & Recovery">Data Backup & Recovery</option>
                          <option value="Network Monitoring & Patching">Network Monitoring & Patching</option>
                          <option value="Cloud Administration">Cloud Administration</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Current Challenges
                        </label>
                        <textarea
                          rows="3"
                          required
                          placeholder="Tell us about any current IT issues or setup requirements..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-600/30 hover:shadow-orange-600/40 mt-2"
                      >
                        Request IT Audit
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}